/**
 * Workflow Card Component
 *
 * Per-resource card displayed on the Workflow Dashboard. Shows the
 * entity name, workspace, completion percentage, step indicator,
 * and opens the entity detail modal (lakehouse / warehouse) on click.
 *
 * @module modules/workflows/components
 * @spec 135-ui-data-alignment
 * @story US2 - Workflow Guidance
 * @constitution v1.12.0 - Principle XXXVII (Code Header Standards)
 */

import React from 'react';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import type { WorkflowResourceStatus } from '../../../shared/types/workflow';
import { WorkflowStepIndicator } from './WorkflowStepIndicator';
import { AppTooltip } from '../../../components/ui/AppTooltip';

// =============================================================================
// PROPS
// =============================================================================

export interface WorkflowCardProps {
  /** Workflow status for a single resource */
  resource: WorkflowResourceStatus;
  /** Open the entity detail modal (lakehouse / warehouse) for this resource. */
  onOpenDetail?: (resource: WorkflowResourceStatus) => void;
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * WorkflowCard — Per-resource workflow progress card for the dashboard.
 *
 * Clicking the title opens the entity detail modal (lakehouse detail /
 * warehouse assignment); the footer links to the next workflow step.
 */
export const WorkflowCard: React.FC<WorkflowCardProps> = ({ resource, onOpenDetail }) => {
  const {
    currentStep,
    totalSteps,
    completionPct,
    isFullyComplete,
    displayName,
    workspaceName,
    steps,
  } = resource;

  // Find next incomplete step for action link
  const nextStep = steps.find((s) => !s.isComplete);

  return (
    <div className="rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-bg-card)] p-5 shadow-[var(--shadow-md)] transition-shadow hover:shadow-[var(--shadow-lg)]">
      {/* Header */}
      <div className="mb-3 flex items-start justify-between">
        <div className="min-w-0 flex-1">
          <button
            type="button"
            onClick={() => onOpenDetail?.(resource)}
            className="block w-full truncate text-left text-sm font-semibold text-[var(--color-text-primary)] transition-colors hover:text-[var(--color-accent)]"
            title={`Open ${displayName} detail`}
          >
            {displayName}
          </button>
          <p className="truncate text-xs text-[var(--color-text-secondary)]" title={workspaceName}>
            {workspaceName}
          </p>
        </div>

        {/* Completion Badge (T087) */}
        {isFullyComplete ? (
          <AppTooltip title={`${totalSteps} of ${totalSteps} steps complete`}>
            <CheckCircle2 className="ml-2 h-5 w-5 shrink-0 text-[var(--color-status-success)]" aria-label="Fully onboarded" />
          </AppTooltip>
        ) : (
          <AppTooltip title={`${currentStep - 1} of ${totalSteps} steps complete`}>
            <span className="ml-2 shrink-0 text-xs font-medium text-[var(--color-accent)]">
              {completionPct.toFixed(0)}%
            </span>
          </AppTooltip>
        )}
      </div>

      {/* Progress Bar (T088) */}
      <AppTooltip title={`${completionPct.toFixed(0)}% complete${nextStep ? ` — Next: ${nextStep.name}` : ''}`}>
        <div className="mb-3 h-1.5 w-full cursor-help rounded-full bg-[var(--color-bg-subtle)]">
          <div
            className="h-1.5 rounded-full transition-all"
            style={{
              width: `${completionPct}%`,
              backgroundColor: isFullyComplete ? 'var(--color-status-success)' : 'var(--color-accent)',
            }}
            role="progressbar"
            aria-valuenow={completionPct}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`${completionPct.toFixed(0)}% complete`}
          />
        </div>
      </AppTooltip>

      {/* Step Indicator */}
      <div className="mb-3">
        <WorkflowStepIndicator
          steps={steps}
          currentStep={currentStep}
          showLabels={true}
          size="md"
        />
      </div>

      {/* Action Footer */}
      <div className="flex items-center justify-between border-t border-[var(--color-border)] pt-2">
        <span className="text-xs text-[var(--color-text-secondary)]">
          {isFullyComplete
            ? `All ${totalSteps} steps complete`
            : `Step ${currentStep} of ${totalSteps}`}
        </span>

        {nextStep && (
          <AppTooltip title={`Open ${displayName} to continue: ${nextStep.name}`}>
            <button
              type="button"
              onClick={() => onOpenDetail?.(resource)}
              className="inline-flex items-center gap-1 text-xs font-medium text-[var(--color-accent)] hover:underline"
            >
              {nextStep.name}
              <ArrowRight className="h-3 w-3" aria-hidden="true" />
            </button>
          </AppTooltip>
        )}
      </div>
    </div>
  );
};

export default WorkflowCard;
